import { useEffect, useMemo, useState } from "react";

const PREFIX = "vw-guide-urgency:";
const storageKey = (stepKey) => `${PREFIX}${stepKey}`;

function formatClock(totalSec) {
  const s = Math.max(0, Math.floor(totalSec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

/** Alle Begleitungs-Timer löschen (z. B. nach TEMP Reset). */
export function clearGuideUrgencyTimers() {
  try {
    const keys = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(PREFIX)) keys.push(k);
    }
    keys.forEach((k) => sessionStorage.removeItem(k));
    window.dispatchEvent(new Event("vw-guide-urgency-clear"));
  } catch (_) {
    /* ignore */
  }
}

/**
 * Countdown pro Begleitungs-Schritt — Urgency ohne Auto-Skip.
 * compact/calm: weniger Alarm für neue Spieler.
 */
const GuideUrgencyTimer = ({
  stepKey,
  urgencySec = 90,
  label = "Nächster Schritt",
  compact = false,
  calm = false,
}) => {
  const budget = Math.max(20, Number(urgencySec) || 90);
  const [left, setLeft] = useState(budget);
  const [epoch, setEpoch] = useState(0);

  useEffect(() => {
    if (!stepKey) return undefined;
    const key = storageKey(stepKey);
    let started = Number(sessionStorage.getItem(key) || 0);
    if (!started) {
      started = Date.now();
      sessionStorage.setItem(key, String(started));
    }
    const tick = () => {
      const elapsed = Math.floor((Date.now() - started) / 1000);
      setLeft(Math.max(0, budget - elapsed));
    };
    tick();
    const id = window.setInterval(tick, 250);
    return () => clearInterval(id);
  }, [stepKey, budget, epoch]);

  useEffect(() => {
    if (!stepKey) return undefined;
    const onClear = () => setEpoch((n) => n + 1);
    window.addEventListener("vw-guide-urgency-clear", onClear);
    return () => window.removeEventListener("vw-guide-urgency-clear", onClear);
  }, [stepKey]);

  const ratio = useMemo(() => (budget ? left / budget : 0), [left, budget]);
  const expired = left <= 0;
  const hot = left > 0 && left <= Math.min(20, Math.floor(budget * 0.25));

  const barColor = expired
    ? calm
      ? "bg-amber-500"
      : "bg-red-500"
    : hot
      ? "bg-orange-500"
      : ratio > 0.45
        ? "bg-emerald-500"
        : "bg-amber-500";

  if (compact) {
    return (
      <div className="rounded-xl border border-amber-200/70 bg-white/50 dark:bg-black/20 p-2.5">
        <div className="flex items-center justify-between gap-2 mb-1">
          <span className="text-[10px] uppercase tracking-wider opacity-50">{label}</span>
          <span
            className={`font-black tabular-nums text-lg ${
              expired && !calm ? "text-red-600" : hot ? "text-orange-600" : ""
            }`}
          >
            {expired ? "0:00" : formatClock(left)}
          </span>
        </div>
        <div className="h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
          <div className={`h-full ${barColor}`} style={{ width: `${Math.round(ratio * 100)}%` }} />
        </div>
      </div>
    );
  }

  return (
    <div
      className={`rounded-2xl border p-3 mb-4 ${
        expired && !calm
          ? "border-red-400 bg-red-50 dark:bg-red-950/40"
          : hot
            ? "border-orange-400 bg-orange-50 dark:bg-orange-950/30"
            : "border-amber-300/80 bg-white/60 dark:bg-black/20"
      }`}
    >
      <div className="flex flex-wrap items-end justify-between gap-2 mb-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest opacity-60">Zeitfenster</div>
          <div className="text-sm font-semibold opacity-80">{label}</div>
        </div>
        <div
          className={`font-black tabular-nums tracking-tight ${
            expired && !calm
              ? "text-3xl text-red-600"
              : hot
                ? "text-3xl text-orange-600"
                : "text-3xl text-gray-900 dark:text-white"
          }`}
        >
          {expired ? "0:00" : formatClock(left)}
        </div>
      </div>
      <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
        <div
          className={`h-full transition-all duration-300 ${barColor}`}
          style={{ width: `${Math.round(ratio * 100)}%` }}
        />
      </div>
      {!calm ? (
        <p className="mt-2 text-xs opacity-80">
          {expired
            ? "Zeit um — erledige diesen Schritt jetzt."
            : hot
              ? "Gleich vorbei — entscheide dich und geh weiter."
              : "Bleib im Rhythmus: Schritt erledigen, dann weiter."}
        </p>
      ) : null}
    </div>
  );
};

export default GuideUrgencyTimer;
